import MethodCategorySelection from '../../MethodSelection';
import { CurrencySelection } from '../../CurrencySelection';
import {
  PaymentOptionsFlowContext,
  usePaymentOptionsFlow,
} from './context/PaymentOptionsFlowContext';
import { PaymentOptionsFlowStep } from './utils';
import { PageFields_CurrenciesPage_Fragment as CurrenciesPageFragment } from '../../../graphql/generated';
import { Currency } from '../../CurrencySelection/CurrencySelection';
import { AnimatePresence } from 'framer-motion';
import { scrollToTop } from '../../../utils';
import { useState } from 'react';
import { PaymentMethod } from '../../../graphql/customTypes';
import { SolanaPayWalletSelection } from '../../SolanaPayWalletSelection';

export interface PaymentOptionsFlowProps {
  data: CurrenciesPageFragment['pageData'];
  onSelectionError: () => void;
}

export default function PaymentOptionsFlow({
  data,
  onSelectionError,
}: PaymentOptionsFlowProps) {
  const {
    cryptoCurrencies,
    fiatTotal,
    fiatCurrency,
    binancePayMethod: isBinancePayEnabledForMerchant,
    expiresAt,
    merchantName,
    eurAmount,
    travelRuleEnabled,
    billType,
  } = data;

  const value = usePaymentOptionsFlow();

  const { step, animationDirection, goToStep, goBack } = value;

  const currencies = mapCurrencies(cryptoCurrencies);

  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(
    null
  );

  const renderStep = () => {
    switch (step) {
      case PaymentOptionsFlowStep.MethodSelection:
        return (
          <MethodCategorySelection
            key="method-category-selection"
            billExpiresAt={expiresAt}
            merchantName={merchantName ?? ''}
            amount={fiatTotal}
            currency={fiatCurrency}
            eurAmount={eurAmount ?? ''}
            isBinancePayEnabledForMerchant={
              isBinancePayEnabledForMerchant ?? false
            }
            currencies={currencies}
            travelRuleEnabled={travelRuleEnabled}
            billType={billType}
            onSelection={(category) => {
              if (category === 'exchange' || category === 'wallet') {
                goToStep(PaymentOptionsFlowStep.CurrencySelection);
                setSelectedMethod(
                  category === 'exchange'
                    ? PaymentMethod.EXCHANGE
                    : PaymentMethod.WALLET
                );
              }
            }}
            onSelectionError={onSelectionError}
          />
        );
      case PaymentOptionsFlowStep.CurrencySelection:
        if (!selectedMethod) {
          return null;
        }

        return (
          <CurrencySelection
            key="currency-selection"
            data={data}
            selectedMethod={selectedMethod}
            onSelectionError={onSelectionError}
            onBack={goBack}
            onSolanaPaySelected={() => {
              goToStep(PaymentOptionsFlowStep.SolanaPayWalletSelection);
            }}
          />
        );

      case PaymentOptionsFlowStep.SolanaPayWalletSelection:
        return (
          <SolanaPayWalletSelection
            key="solana-wallet-selection"
            data={data}
            onSelectionError={onSelectionError}
            onGoBack={goBack}
          />
        );
    }
  };

  return (
    <PaymentOptionsFlowContext.Provider value={value}>
      <AnimatePresence
        custom={animationDirection}
        mode="wait"
        onExitComplete={scrollToTop}
      >
        {renderStep()}
      </AnimatePresence>
    </PaymentOptionsFlowContext.Provider>
  );
}

const mapCurrencies = (
  currencies: CurrenciesPageFragment['pageData']['cryptoCurrencies'] | null
) => {
  return (
    currencies
      ?.map((currency) => {
        if (!currency) {
          return null;
        }

        return {
          name: currency.currency ?? '',
          symbol: currency.currency ?? '',
          blockchains:
            currency.blockchains?.map((blockchain) => {
              if (!blockchain) {
                return null;
              }

              return {
                chainSymbol: blockchain.blockchain ?? '',
                available: blockchain.available ?? false,
                reason: blockchain.reason ?? '',
              };
            }) ?? [],
        };
      })
      .filter((currency) => currency !== null)
      .map((currency) => currency as Currency) ?? []
  );
};
